import { Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/Badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { cn } from '@/utils/cn'
import type { Lesson, Period, WeekData } from '../types'
import { defaultDay, formatTime, indexLessons, lessonKey, nowAndNext, timeRange, weekdayName } from '../week'
import { LessonCard, type LessonView } from './LessonCard'

interface WeekGridProps {
  week: WeekData
  view: LessonView
  /** Accessible name of the table. */
  caption: string
  /** When set, every lesson cell becomes a button (empty cells show "+") that opens the editor. */
  onCellClick?: (dayOfWeek: number, period: Period, lesson: Lesson | undefined) => void
  /** Highlight today and the current / next period ("Now" / "Next" chips). */
  highlightNow?: boolean
  now?: Date
}

/**
 * The weekly timetable: a table (rows = periods, breaks as thin labelled rows; columns = open
 * weekdays) from md up and for print, day tabs with a vertical list of periods on phones.
 */
export function WeekGrid({ week, view, caption, onCellClick, highlightNow = false, now }: WeekGridProps) {
  const { t } = useTranslation('timetable')
  const lessons = indexLessons(week.entries)
  const live = highlightNow ? nowAndNext(week.days, week.periods, now) : null
  const [day, setDay] = useState(() => defaultDay(week.days, now))

  const chipFor = (d: number, p: Period): ReactNode => {
    if (!live || live.today !== d) return null
    if (live.nowPeriodId === p.id)
      return (
        <Badge tone="brand" size="sm">
          {t('Now')}
        </Badge>
      )
    if (live.nextPeriodId === p.id)
      return (
        <Badge tone="info" size="sm">
          {t('Next')}
        </Badge>
      )
    return null
  }

  const describe = (d: number, p: Period, lesson: Lesson | undefined): string => {
    const base = { day: weekdayName(d), period: p.name }
    if (!lesson) return t('{{day}}, {{period}}: free — add a lesson', base)
    const subject = lesson.subject.name
    if (lesson.teacher && lesson.room)
      return t('{{day}}, {{period}}: {{subject}} with {{teacher}} in {{room}}', {
        ...base,
        subject,
        teacher: lesson.teacher.name,
        room: lesson.room.name,
      })
    if (lesson.teacher)
      return t('{{day}}, {{period}}: {{subject}} with {{teacher}}', {
        ...base,
        subject,
        teacher: lesson.teacher.name,
      })
    if (lesson.room)
      return t('{{day}}, {{period}}: {{subject}} in {{room}}', { ...base, subject, room: lesson.room.name })
    return t('{{day}}, {{period}}: {{subject}}', { ...base, subject })
  }

  const cell = (d: number, p: Period, compact = false): ReactNode => {
    const lesson = lessons.get(lessonKey(d, p.id))
    const chip = chipFor(d, p)
    const current = live?.today === d && live.nowPeriodId === p.id
    const ring = current ? 'ring-2 ring-brand-500 ring-offset-1 ring-offset-surface' : ''
    if (onCellClick) {
      return (
        <button
          type="button"
          onClick={() => onCellClick(d, p, lesson)}
          aria-label={describe(d, p, lesson)}
          className={cn(
            'block h-full w-full rounded-control transition-[transform,box-shadow] hover:shadow-raised active:translate-y-px',
            ring,
          )}
        >
          {lesson ? (
            <LessonCard lesson={lesson} view={view} chip={chip} compact={compact} />
          ) : (
            <span className="relative grid h-full min-h-16 place-items-center rounded-control border border-dashed border-line text-subtle transition-colors hover:border-brand-300 hover:bg-brand-50/60 hover:text-brand-600">
              <Plus aria-hidden="true" className="size-4 print:hidden" />
              {chip ? <span className="absolute top-1.5 right-1.5">{chip}</span> : null}
            </span>
          )}
        </button>
      )
    }
    if (lesson) {
      return (
        <div className={cn('h-full rounded-control', ring)}>
          <LessonCard lesson={lesson} view={view} chip={chip} compact={compact} />
        </div>
      )
    }
    return (
      <div
        className={cn(
          'relative flex h-full min-h-16 items-center justify-center rounded-control border border-dashed border-line/80 text-2xs text-subtle',
          ring,
        )}
      >
        <span className="sr-only">{t('Free')}</span>
        {chip ? <span className="absolute top-1.5 right-1.5">{chip}</span> : null}
      </div>
    )
  }

  return (
    <>
      <div className="hidden overflow-x-auto md:block print:block">
        <table className="w-full min-w-[44rem] table-fixed border-separate border-spacing-1.5">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr>
              <th
                scope="col"
                className="w-28 px-2 text-left text-2xs font-medium tracking-wide text-muted uppercase"
              >
                {t('Period')}
              </th>
              {week.days.map((d) => (
                <th
                  key={d}
                  scope="col"
                  className={cn(
                    'rounded-control px-2 py-2 text-left text-xs font-semibold text-ink-soft',
                    live?.today === d && 'bg-brand-50 text-brand-700',
                  )}
                >
                  <span className="flex items-center gap-2">
                    {weekdayName(d)}
                    {live?.today === d ? (
                      <Badge tone="brand" size="sm" className="print:hidden">
                        {t('Today')}
                      </Badge>
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {week.periods.map((p) =>
              p.isBreak ? (
                <tr key={p.id}>
                  <th scope="row" className="px-2 text-left text-2xs font-normal text-muted tabular">
                    {timeRange(p)}
                  </th>
                  <td colSpan={week.days.length}>
                    <div className="flex items-center gap-3 text-2xs font-medium tracking-wide text-muted uppercase">
                      <span aria-hidden="true" className="h-px flex-1 bg-line" />
                      {p.name}
                      <span aria-hidden="true" className="h-px flex-1 bg-line" />
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={p.id}>
                  <th scope="row" className="px-2 py-1 text-left align-top">
                    <span className="block text-xs font-semibold text-ink">{p.name}</span>
                    <span className="block text-2xs font-normal text-muted tabular">{timeRange(p)}</span>
                  </th>
                  {week.days.map((d) => (
                    <td key={d} className="h-20 align-top">
                      {cell(d, p)}
                    </td>
                  ))}
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden print:hidden">
        <Tabs value={day === undefined ? undefined : String(day)} onValueChange={(v) => setDay(Number(v))}>
          <TabsList aria-label={t('Day of the week')}>
            {week.days.map((d) => (
              <TabsTrigger key={d} value={String(d)} aria-label={weekdayName(d)}>
                {weekdayName(d, 'short')}
                {live?.today === d ? (
                  <span aria-hidden="true" className="size-1.5 rounded-full bg-brand-500" />
                ) : null}
              </TabsTrigger>
            ))}
          </TabsList>
          {week.days.map((d) => (
            <TabsContent key={d} value={String(d)}>
              <ol className="space-y-2" aria-label={weekdayName(d)}>
                {week.periods.map((p) =>
                  p.isBreak ? (
                    <li
                      key={p.id}
                      className="flex items-center gap-2 py-1 text-2xs font-medium tracking-wide text-muted uppercase"
                    >
                      <span aria-hidden="true" className="h-px flex-1 bg-line" />
                      {p.name} · {timeRange(p)}
                      <span aria-hidden="true" className="h-px flex-1 bg-line" />
                    </li>
                  ) : (
                    <li key={p.id} className="flex gap-3">
                      <div className="w-16 shrink-0 pt-2 text-xs">
                        <span className="block font-semibold text-ink">{p.name}</span>
                        <span className="block text-muted tabular">{formatTime(p.startTime)}</span>
                      </div>
                      <div className="min-w-0 flex-1">{cell(d, p, true)}</div>
                    </li>
                  ),
                )}
              </ol>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </>
  )
}
